/**
 * Backward and forward citation searching on OpenAlex.
 *
 * Starting from "seed" articles you already trust, **backward** chasing collects the
 * works each seed cites (its reference list) and **forward** chasing collects the works
 * that cite it. Both are standard supplementary search methods for systematic reviews;
 * Haddaway et al.'s Citationchaser (2022) does the same job on Lens.org data.
 *
 * Data: the OpenAlex API (api.openalex.org). A single work is a free "singleton" call;
 * each list call (`filter=…`) is one credit of the keyless daily budget, which OpenAlex
 * shares between everyone behind the same IP address. OpenAlex's reference lists are
 * what OpenAlex has extracted from publisher metadata and PDFs — they can be shorter
 * than the printed list — so the result is a lead list, not a complete search.
 */

import {
  type DoiLine,
  readDoiLines,
} from "@/lib/tool-kit/reference-tools/open-access";
import {
  cleanDoi,
  emptyRecord,
  nameFromText,
  type RefRecord,
} from "@/lib/tool-kit/research-discovery/records";

const API = "https://api.openalex.org/works";
/** The site's own contact address, as OpenAlex's etiquette asks of clients. */
export const OPENALEX_MAILTO = "support@citationeasy.com";

export const MAX_SEEDS = 5;
/** Most cited works fetched per seed; beyond this the list is a sample. */
export const FORWARD_CAP = 200;
/** Most references fetched across all seeds. */
export const BACKWARD_CAP = 500;
export const PAGE_SIZE = 100;

export type ForwardOrder = "newest" | "most-cited";

const WORK_FIELDS = [
  "id",
  "doi",
  "title",
  "display_name",
  "publication_year",
  "publication_date",
  "type",
  "authorships",
  "primary_location",
  "biblio",
  "cited_by_count",
  "language",
];
const SEED_FIELDS = [...WORK_FIELDS, "referenced_works"];

const RE_OPENALEX_ID = /^(?:https?:\/\/openalex\.org\/)?(W\d+)$/i;
const RE_TSV_BREAKS = /[\t\r\n]+/g;

export interface ChaseWork {
  /** "W2128438887" */
  id: string;
  doi: string;
  title: string;
  year: number | null;
  /** First three authors, as OpenAlex displays them. */
  authors: string[];
  authorCount: number;
  venue: string;
  citedBy: number;
  record: RefRecord;
  /** For a seed only: the works it cites. */
  referenced: string[];
}

function str(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function shortId(value: unknown): string {
  const match = RE_OPENALEX_ID.exec(str(value));
  return match ? match[1].toUpperCase() : "";
}

/** RIS reference type for an OpenAlex work `type`. */
const RIS_TYPES: Record<string, string> = {
  article: "JOUR",
  review: "JOUR",
  letter: "JOUR",
  editorial: "JOUR",
  erratum: "JOUR",
  book: "BOOK",
  "book-chapter": "CHAP",
  dissertation: "THES",
  preprint: "UNPB",
  dataset: "DATA",
  report: "RPRT",
};

function authorNames(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((entry) =>
      str(
        (entry as { author?: { display_name?: unknown } })?.author?.display_name
      )
    )
    .filter((name) => name !== "");
}

function toRecord(work: Record<string, unknown>, names: string[]): RefRecord {
  const location = (work.primary_location ?? {}) as Record<string, unknown>;
  const source = (location.source ?? {}) as Record<string, unknown>;
  const biblio = (work.biblio ?? {}) as Record<string, unknown>;
  const doi = cleanDoi(str(work.doi));
  const record = emptyRecord(RIS_TYPES[str(work.type)] ?? "GEN");
  record.authors = names.map(nameFromText);
  record.title = str(work.title) || str(work.display_name);
  record.container = str(source.display_name);
  record.year = work.publication_year ? String(work.publication_year) : "";
  record.date = str(work.publication_date).replaceAll("-", "/");
  record.volume = str(biblio.volume);
  record.issue = str(biblio.issue);
  record.startPage = str(biblio.first_page);
  record.endPage = str(biblio.last_page);
  record.doi = doi;
  record.url = doi ? `https://doi.org/${doi}` : str(work.id);
  record.language = str(work.language);
  record.issn = str(source.issn_l);
  record.database = "OpenAlex";
  record.accession = shortId(work.id);
  return record;
}

/** One OpenAlex work as a ChaseWork; null without an OpenAlex ID. */
export function parseWork(raw: unknown): ChaseWork | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const work = raw as Record<string, unknown>;
  const id = shortId(work.id);
  if (!id) {
    return null;
  }
  const names = authorNames(work.authorships);
  const year = Number(work.publication_year);
  const record = toRecord(work, names);
  const referenced = Array.isArray(work.referenced_works)
    ? work.referenced_works.map(shortId).filter((value) => value !== "")
    : [];
  return {
    id,
    doi: record.doi,
    title: record.title,
    year: Number.isInteger(year) && year > 0 ? year : null,
    authors: names.slice(0, 3),
    authorCount: names.length,
    venue: record.container,
    citedBy: Number(work.cited_by_count) || 0,
    record,
    referenced,
  };
}

export function parseWorkList(body: unknown): ChaseWork[] {
  const results = (body as { results?: unknown } | null)?.results;
  if (!Array.isArray(results)) {
    return [];
  }
  return results.flatMap((raw) => {
    const work = parseWork(raw);
    return work ? [work] : [];
  });
}

/* ------------------------------------------------------------------ URLs */

function base(fields: readonly string[]): string {
  return `select=${fields.join(",")}&mailto=${encodeURIComponent(OPENALEX_MAILTO)}`;
}

/** A seed by DOI — a singleton call, which OpenAlex does not charge for. */
export function seedUrl(doi: string): string {
  return `${API}/doi:${encodeURIComponent(doi)}?${base(SEED_FIELDS)}`;
}

/** Up to 100 works by OpenAlex ID, in one list call. */
export function worksByIdUrl(ids: readonly string[]): string {
  return `${API}?filter=openalex:${ids.join("|")}&per-page=${PAGE_SIZE}&${base(WORK_FIELDS)}`;
}

/** One page of the works that cite `id`. */
export function citedByUrl(
  id: string,
  order: ForwardOrder,
  page: number
): string {
  const sort =
    order === "newest" ? "publication_date:desc" : "cited_by_count:desc";
  return `${API}?filter=cites:${id}&sort=${sort}&per-page=${PAGE_SIZE}&page=${page}&${base(WORK_FIELDS)}`;
}

/* ------------------------------------------------------------------ planning */

export interface SeedLine extends DoiLine {
  state: "queued" | "invalid" | "duplicate" | "over";
}

/** One row per pasted line, saying which will be looked up. */
export function planSeeds(text: string): SeedLine[] {
  let queued = 0;
  return readDoiLines(text).map((line): SeedLine => {
    if (line.doi === null) {
      return { ...line, state: "invalid" };
    }
    if (line.duplicate) {
      return { ...line, state: "duplicate" };
    }
    queued += 1;
    return { ...line, state: queued > MAX_SEEDS ? "over" : "queued" };
  });
}

/** How many cited-by pages a seed needs, given the cap. */
export function forwardPages(citedBy: number): number {
  return Math.ceil(Math.min(citedBy, FORWARD_CAP) / PAGE_SIZE);
}

/**
 * The reference IDs to fetch: every ID the seeds cite, those cited by the most seeds
 * first, cut at `BACKWARD_CAP`. Also returns how many seeds cite each and how many were
 * left out.
 */
export function planBackward(seeds: readonly ChaseWork[]): {
  ids: string[];
  links: Map<string, number>;
  total: number;
} {
  const links = new Map<string, number>();
  for (const seed of seeds) {
    for (const id of new Set(seed.referenced)) {
      links.set(id, (links.get(id) ?? 0) + 1);
    }
  }
  const seedIds = new Set(seeds.map((seed) => seed.id));
  const all = [...links.keys()].filter((id) => !seedIds.has(id));
  all.sort((a, b) => (links.get(b) ?? 0) - (links.get(a) ?? 0));
  return { ids: all.slice(0, BACKWARD_CAP), links, total: all.length };
}

export function batchesOf<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    out.push(items.slice(index, index + size));
  }
  return out;
}

/* ------------------------------------------------------------------ merging */

export interface ChaseHit extends ChaseWork {
  /** How many seeds cite it (backward) or are cited by it (forward). */
  links: number;
}

/** Merge works found for several seeds into one list, counting seed links. */
export function mergeHits(
  found: readonly { work: ChaseWork; seedId: string }[],
  excluded: ReadonlySet<string>
): ChaseHit[] {
  const byId = new Map<string, { work: ChaseWork; seeds: Set<string> }>();
  for (const { work, seedId } of found) {
    if (excluded.has(work.id)) {
      continue;
    }
    const entry = byId.get(work.id) ?? { work, seeds: new Set<string>() };
    entry.seeds.add(seedId);
    byId.set(work.id, entry);
  }
  return [...byId.values()]
    .map(({ work, seeds }) => ({ ...work, links: seeds.size }))
    .sort((a, b) => b.links - a.links || b.citedBy - a.citedBy);
}

/** Fetched references as hits, each with the number of seeds that cite it. */
export function backwardHits(
  works: readonly ChaseWork[],
  links: ReadonlyMap<string, number>
): ChaseHit[] {
  const unique = new Map<string, ChaseWork>();
  for (const work of works) {
    unique.set(work.id, work);
  }
  return [...unique.values()]
    .map((work) => ({ ...work, links: links.get(work.id) ?? 1 }))
    .sort((a, b) => b.links - a.links || b.citedBy - a.citedBy);
}

/** Hits in `a` that also appear in `b`, by OpenAlex ID. */
export function overlapCount(
  a: readonly ChaseHit[],
  b: readonly ChaseHit[]
): number {
  const ids = new Set(b.map((hit) => hit.id));
  return a.filter((hit) => ids.has(hit.id)).length;
}

/** The two lists joined, each work once. */
export function unionHits(
  a: readonly ChaseHit[],
  b: readonly ChaseHit[]
): ChaseHit[] {
  const seen = new Set<string>();
  const out: ChaseHit[] = [];
  for (const hit of [...a, ...b]) {
    if (!seen.has(hit.id)) {
      seen.add(hit.id);
      out.push(hit);
    }
  }
  return out;
}

export function doiList(hits: readonly ChaseHit[]): string {
  return hits
    .map((hit) => hit.doi)
    .filter((doi) => doi !== "")
    .join("\n");
}

/** Tab-separated table for a spreadsheet. */
export function hitsTsv(hits: readonly ChaseHit[]): string {
  const header = [
    "Title",
    "Authors",
    "Year",
    "Venue",
    "DOI",
    "Cited by",
    "Seeds linked",
  ];
  const rows = hits.map((hit) => [
    hit.title,
    hit.authors.join("; ") +
      (hit.authorCount > hit.authors.length ? " et al." : ""),
    hit.year === null ? "" : String(hit.year),
    hit.venue,
    hit.doi,
    String(hit.citedBy),
    String(hit.links),
  ]);
  return [header, ...rows]
    .map((row) =>
      row.map((cell) => cell.replace(RE_TSV_BREAKS, " ")).join("\t")
    )
    .join("\n");
}
